import { supabase } from "@/integrations/supabase/client";
import { ensureFreshSession } from "@/lib/authSession";
import { Sentry } from "@/lib/sentry";

// MetaPerson Creator (Mobile) — Path A embed constants + edge-function bridge.
// The frontend NEVER touches AVATARSDK credentials: it asks metaperson-token
// (Phase 1) for a short-lived access_token and posts THAT into the iframe.

export const METAPERSON_MOBILE_URL =
  "https://mobile.metaperson.avatarsdk.com/generator";
export const METAPERSON_ORIGIN = "https://mobile.metaperson.avatarsdk.com";

export type MetaPersonGender = "male" | "female" | "";

export interface MetaPersonExportResult {
  url: string;
  avatarCode: string;
  gender: string;
  // Opaque Creator state blob (returned by model_exported); forwarded as-is
  // if a future phase wants to persist/restore an editing session.
  avatarState?: unknown;
}

// Mints the Creator access token via the Phase 1 edge function.
// Response shape (metaperson-token): { access_token, expires_in }.
export async function fetchCreatorToken(): Promise<string> {
  const { data, error } = await supabase.functions.invoke("metaperson-token", {
    body: {},
  });
  if (error) throw error;
  const token = data && typeof (data as Record<string, unknown>).access_token === "string"
    ? ((data as Record<string, unknown>).access_token as string)
    : null;
  if (!token) throw new Error("metaperson-token: missing access_token");
  return token;
}

// Imports the exported GLB server-side via the Phase 1 edge function.
// Request body (metaperson-import): { exportedUrl, avatarCode, gender }.
// Response shape: { avatar: <user_avatars row> }.
//
// RESILIENCE (12/06, P0): the user spends 5–10 min inside the Creator iframe
// before export fires — long enough for the access token to expire, and the
// import fn is JWT-verified (it would 401, losing the avatar the user just
// built). So: refresh the session FIRST, then up to 3 attempts with backoff.
// The fn's storage upload happens BEFORE its user_avatars insert and uses
// upsert on the same path, so a retry after a half-applied attempt converges
// instead of corrupting (worst case: a duplicate library row, never a
// dangling GLB pointer). Throwing after the retries keeps the existing
// recoverable error screen + "try again" (lastExportRef) intact.
const IMPORT_ATTEMPTS = 3;
const IMPORT_BACKOFF_MS = 1500;

export async function importGeneratedAvatar(
  result: MetaPersonExportResult,
): Promise<Record<string, unknown>> {
  const sessionOk = await ensureFreshSession();
  if (!sessionOk) {
    // Genuinely signed out — the import WILL 401; fail fast with a clear
    // marker instead of burning retries.
    throw new Error("metaperson-import: no authenticated session");
  }

  let lastErr: unknown = null;
  for (let attempt = 1; attempt <= IMPORT_ATTEMPTS; attempt++) {
    if (attempt > 1) {
      await new Promise((r) => setTimeout(r, IMPORT_BACKOFF_MS * Math.pow(2, attempt - 2)));
      await ensureFreshSession();
    }
    try {
      const { data, error } = await supabase.functions.invoke("metaperson-import", {
        body: {
          exportedUrl: result.url,
          avatarCode: result.avatarCode,
          gender: result.gender,
        },
      });
      if (error) throw error;
      const avatar = data && (data as Record<string, unknown>).avatar;
      if (!avatar) throw new Error("metaperson-import: missing avatar in response");
      return avatar as Record<string, unknown>;
    } catch (err) {
      lastErr = err;
      console.warn("[metapersonCreator] import attempt " + attempt + "/" + IMPORT_ATTEMPTS + " failed:", err);
    }
  }
  try {
    Sentry.captureException(lastErr instanceof Error ? lastErr : new Error(String(lastErr)), {
      tags: { feature: "metaperson_import_failed" },
      extra: { avatarCode: result.avatarCode },
    });
  } catch { /* Sentry not initialized in dev */ }
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}

// ---------------------------------------------------------------------------
// Reload resilience: the "pending" avatar bridge
// ---------------------------------------------------------------------------
// The Creator emits 'model_generated' right AFTER generation but BEFORE export
// (the user may keep tweaking, reload, or background the tab in between). We
// stash the avatarCode the moment generation lands so the flow can offer
// "resume" — reopening the SAME avatar via show_avatar instead of forcing a
// second selfie. Cleared on a successful import or on cancel. Best-effort:
// storage failures never block avatar creation.

const PENDING_KEY = "vyv_metaperson_pending";
const PENDING_TTL_MS = 24 * 60 * 60 * 1000; // 24h

export interface MetaPersonPending {
  avatarCode: string;
  gender: string;
  ts: number;
}

export function savePendingAvatar(avatarCode: string, gender: string): void {
  if (!avatarCode) return; // nothing to resume without a code
  try {
    const payload: MetaPersonPending = { avatarCode, gender: gender || "", ts: Date.now() };
    localStorage.setItem(PENDING_KEY, JSON.stringify(payload));
  } catch {
    /* storage disabled / quota exceeded — resume is best-effort */
  }
}

// Pure read: returns null for missing / malformed / expired (>24h) entries
// WITHOUT writing, so it's safe to call from a render-time state initializer.
// Stale entries are reclaimed by clearPendingAvatar() on cancel / import, or
// overwritten by the next savePendingAvatar().
export function readPendingAvatar(): MetaPersonPending | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<MetaPersonPending> | null;
    if (!parsed || typeof parsed.avatarCode !== "string" || !parsed.avatarCode) return null;
    if (typeof parsed.ts !== "number" || Date.now() - parsed.ts > PENDING_TTL_MS) return null;
    return {
      avatarCode: parsed.avatarCode,
      gender: typeof parsed.gender === "string" ? parsed.gender : "",
      ts: parsed.ts,
    };
  } catch {
    return null;
  }
}

export function clearPendingAvatar(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}
